import { isEmpty, cloneDeep } from "lodash";
import { SurveyPDF } from "survey-pdf";
import AWSs3Mixin from "@/mixins/awss3.js";
import ClientWebformService from "@/services/client-webform";
import { DateTime } from "luxon";
import uniqid from "uniqid";
import { Converter } from "showdown";
import EXTENSIONS from "@/constants/extensions";

const DEFAULT_IMAGE_WIDTH = "200px";
const DEFAULT_IMAGE_HEIGHT = "300px";
const FALLBACK_MB = 100;
const FALLBACK_MAX = FALLBACK_MB * 1024 * 1024; // bytes
var converter = new Converter();

export default {
    mixins: [AWSs3Mixin],
    computed: {
        checkExtensionsSMSEnabled() {
            return (
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_SMS) ||
                this._isExtensionEnabled(EXTENSIONS.CODE.CODE_2_WAY_MESSAGING)
            );
        }
    },
    data() {
        return {
            DEFAULT_IMAGE_WIDTH,
            DEFAULT_IMAGE_HEIGHT,
            FALLBACK_MAX
        };
    },
    methods: {
        /**
         * checkCanEdit
         * To check whether to hide/display the action edit
         * @param {Object} webform
         * @returns {Boolean}
         */
        checkCanEdit(webform) {
            return (
                isEmpty(webform.submitted_at) &&
                webform.encoded_id &&
                !webform.is_sent &&
                webform.isactive &&
                webform.isEditable &&
                webform.isEditable.status
            );
        },
        /**
         * checkCanDelete
         * To check whether to hide/display the action delete
         * @param {Object} webform
         * @returns {Boolean}
         */
        checkCanDelete(webform) {
            return isEmpty(webform.submitted_at);
        },
        /**
         * checkCanSend
         * To check whether to hide/display the action send
         * @param {Object} webform
         * @returns {Boolean}
         */
        checkCanSend(webform) {
            return isEmpty(webform.submitted_at);
        },
        /**
         * checkCanPrint
         * To check whether to hide/display the action print
         * @param {Object} webform
         * @returns {Boolean}
         */
        checkCanPrint(webform) {
            // VET-2303 - On this issue, user will be able to print the form PDF even not been submitted yet
            return true;
        },
        /**
         * @description To check whether can send email/sms when the webform content is empty
         * @param {Object} webformContent
         * @returns
         */
        checkCanSendWebform(webformContent, action = "send") {
            if (isEmpty(webformContent)) {
                this._message(
                    `Unable to ${action} form without content.`,
                    "danger"
                );
                return false;
            }
            return true;
        },
        /**
         * printPDF
         * To open the s3_link in a new tab
         * But, if it empty, regenerate the pdf by using the surveyJS PDF library
         * @param {Object} webform
         */
        async printWebformPDF(webform) {
            let webformLink = webform.s3_link;
            if (!webformLink) {
                // Webform content empty as from document list will not have webform_content
                try {
                    let webformContent = webform.webform_content;
                    let webformAnswers = webform.webform_answers;
                    let isSubmitted = webform.submitted_at;

                    if (isEmpty(webformContent)) {
                        const fetchDetails = await ClientWebformService.getWebformDetail(
                            webform.id
                        );

                        if (!fetchDetails) {
                            throw new Error("Unable to print form");
                        }
                        webform = fetchDetails;
                        webformContent = fetchDetails.webform_content;
                        webformAnswers = fetchDetails.webform_answers;
                        isSubmitted = fetchDetails.submitted_at;
                    }

                    // If has answer or submitted even answer empty, will store in S3 and DB
                    const isStoreInS3 = !isEmpty(webformAnswers) || isSubmitted;

                    webformLink = await this.generatePdf(
                        webform,
                        webformContent,
                        webformAnswers
                    );

                    if (isStoreInS3) {
                        const response = await ClientWebformService.updateWebform(
                            webform.id,
                            {
                                client_id: webform.client_id,
                                s3_link: webformLink,
                                template_id: webform.template_id,
                                title: webform.title
                            }
                        );

                        if (response) {
                            this.$store.commit("webform/setDetails", response);
                            this.$store.commit(
                                "webform/setEditDetails",
                                response
                            );
                        }
                    }

                    if (!webformLink) {
                        throw new Error("Unable to print form");
                    }
                } catch (error) {
                    this._message("Unable to print form", "danger");
                    console.error(error.message);
                    return;
                }
            }

            window.open(webformLink, "_blank");
        },
        /**
         * @description To generate the PDF webform by given webform content and answer JSON
         * @param {Object} webform
         * @param {String} webform.title
         * @param {String} webform.id id of tbl_webform (webform) not id template webform
         * @param {String} webform.client_id
         * @param {Object} webformContent
         * @param {Object} webformAnswer
         * @returns {String} webformPDFLink
         */
        async generatePdf(webform, webformContent, webformAnswer) {
            let self = this;
            try {
                // use clone deep to prevent conversion from s3 link to base64 sent to barramundi on endpoint save
                let webformPDFLink = "";
                let surveyJson = cloneDeep(webformContent);
                let answerJson = cloneDeep(webformAnswer);

                surveyJson = await this.processSurveyJson(surveyJson);
                answerJson = await this.processSurveyJson(answerJson);
                // PDF options
                const exportToPdfOptions = {
                    haveCommercialLicense: true, // Setting to true to remove the commercial usage warning on pdf
                    textFieldRenderAs: "multiLine", // for multiple input question
                    // applyImageFit: true, // if set to false, the size of the images will not even // removed as it causing the images in PDF generated from safari missing
                    htmlRenderAs: "standard"
                };
                let surveyPdf = new SurveyPDF(surveyJson, exportToPdfOptions);
                surveyPdf.onTextMarkdown.add(this.doMarkdown);
                surveyPdf.mode = "display"; // To disable the input in pdf format
                surveyPdf.data = answerJson;
                await surveyPdf.raw("blob").then(async function (blob) {
                    // Append the filename to the blob
                    let generatedUniqid = self.generateUniqid();

                    const fileName = self._sanitizeFilename(
                        webform.title.substring(0, 30) + "_" + generatedUniqid,
                        true
                    );
                    blob.name = `${fileName}.pdf`;
                    // Upload the file to S3
                    const response = await self.storeS3(
                        [{ file: blob, fileName }],
                        {
                            name: "webform",
                            id: webform.id,
                            path: `webform/client/${webform.client_id}/${webform.id}`
                        }
                    );
                    // Link S3
                    webformPDFLink = response[0].path;
                });

                return webformPDFLink;
            } catch (e) {
                console.error(e);
            }
        },
        /**
         * @description
         * Remove the imageWidth from the question image as it makes the pdf generator error
         * Alter the HTML tag to fix issue VET-3876
         * @param {Object} surveyJson
         * SurveyJS Ticket: Issue T14533 : https://surveyjs.answerdesk.io/ticket/details/t14533/invalid-arguments-passed-to-jspdf-text
         * @returns {Object} surveyJson
         */
        async processSurveyJson(surveyJson) {
            if (typeof surveyJson !== "object" || surveyJson === null) {
                return surveyJson;
            }

            const imageFileExtensionsRegex = /\b(?:https?:\/\/)?\S+\.(jpg|jpeg|png|gif|webp)\b/;

            const processObject = async obj => {
                for (let key in obj) {
                    switch (key) {
                        case "type":
                            if (
                                obj[key] === "image" &&
                                obj["imageWidth"] === "auto" &&
                                obj["imageHeight"] === "auto"
                            ) {
                                obj["imageWidth"] = this.DEFAULT_IMAGE_WIDTH;
                                obj["imageHeight"] = this.DEFAULT_IMAGE_HEIGHT;
                            } else if (obj[key] === "html" && obj["html"]) {
                                obj["html"] = this.processHtml(obj["html"]);
                            }
                            break;
                        case "imageWidth":
                        case "imageHeight":
                            if (obj[key] === "100%") {
                                obj[key] = key.includes("Width")
                                    ? this.DEFAULT_IMAGE_WIDTH
                                    : this.DEFAULT_IMAGE_HEIGHT;
                            }
                            break;
                        case "html": {
                            if (/<li>/i.test(obj[key])) {
                                obj[key] = this.removeHtmlTagsWithinLi(
                                    obj[key]
                                );
                            }

                            // VET-10890 - Remove the 2nd and next <br> if has multiple <br>
                            const regextDetextBr = /(<br\s*\/?>\s*){2,}/gi;
                            if (regextDetextBr.test(obj[key])) {
                                obj[key] = obj[key].replace(
                                    regextDetextBr,
                                    "<br>"
                                );
                            }

                            break;
                        }
                        case "description":
                            // Need to discuss whether to remove the description from using formatting text element, below is just temporary fix to remove all html tag
                            obj[key] = obj[key]
                                .replace(/<\/?[^>]+(>|$)/g, "")
                                .replaceAll(/&#39;/g, "'");
                            break;
                        default:
                            if (
                                typeof obj[key] === "string" &&
                                this.checkIsURL(obj[key])
                            ) {
                                obj[key] = this.addTimestampToUrl(obj[key]);
                                if (
                                    imageFileExtensionsRegex.test(obj[key]) &&
                                    !["type", "name"].includes(key)
                                ) {
                                    const res = await this.convertFileURLToBase64(
                                        obj[key]
                                    );

                                    obj[key] = res?.based64 ?? obj[key];

                                    if (key === "logo") {
                                        obj["logoWidth"] = `${res.width}px`;
                                        obj["logoHeight"] = `${res.height}px`;
                                    }
                                }
                            } else if (
                                typeof obj[key] === "object" &&
                                obj[key] !== null
                            ) {
                                await processObject(obj[key]);
                            }
                    }
                }
            };

            await processObject(surveyJson);
            return surveyJson;
        },
        removeHtmlTagsWithinLi(htmlString) {
            // This regex targets an opening <li> tag followed by any content and then an HTML tag, capturing the <li> tag and any text within it up to the first HTML tag.
            let regex = /<li>(.*?)<\/li>/gs;

            return htmlString.replace(regex, (match, innerContent) => {
                // Remove all HTML tags from the captured inner content of the <li> element
                let cleanedContent = innerContent.replace(/<[^>]*>/g, "");
                // Return the <li> tag with the cleaned content
                return `<li>${cleanedContent}</li>`;
            });
        },
        processHtml(htmlString) {
            const tempDiv = document.createElement("div"); // Generate the HTML node
            tempDiv.innerHTML = htmlString;

            tempDiv.querySelectorAll("table").forEach(tableNode => {
                tableNode.querySelectorAll("*").forEach(node => {
                    const parent = node.parentNode;
                    if (parent && node.tagName === "P") {
                        while (node.firstChild) {
                            parent.insertBefore(node.firstChild, node);
                        }
                        parent.removeChild(node);
                    }
                });
            });

            // Remove unwanted symbols and characters
            const cleanedHTML = tempDiv.innerHTML
                .replace(/[\n\t\r]/g, "") // Remove newlines, tabs, and carriage returns
                .replace(/&nbsp;/g, " ") // Replace non-breaking spaces with a regular space
                .replace(/\u200B/g, ""); // Remove zero-width spaces
            return cleanedHTML;
        },

        /**
         * @description To convert the base64 to S3 link
         * Can be used by both webform content and answer
         * @param {Object} surveyJson
         * @returns
         */
        async convertBase64ToS3(surveyJson, params = null) {
            let self = this;
            for (var key in surveyJson) {
                if (
                    typeof surveyJson[key] === "string" &&
                    surveyJson[key].includes("data:") &&
                    surveyJson[key].includes("base64,")
                ) {
                    let originalFileName = surveyJson.name
                        ? surveyJson.name.substring(
                              0,
                              surveyJson.name.lastIndexOf(".")
                          )
                        : ""; // for uploaded file during answer submission has its own filename or its question name
                    let fileName = originalFileName ? originalFileName : key;
                    fileName = self._sanitizeFilename(fileName);

                    const byteLength = new Blob([fileName]).size;
                    if (byteLength > 100) {
                        // put 100 just to be safe
                        const webformId = self.webformId || params.webformId;
                        const jsonType = `_${params.jsonType}` || ""; // answer / question
                        fileName = `webform_${webformId}${jsonType}`; // issue with AWS return error, key too long : VET-7968
                    }

                    const response = await this.storeToS3(
                        fileName,
                        surveyJson[key],
                        params
                    );
                    surveyJson[key] = response;
                } else if (typeof surveyJson[key] === "object") {
                    await this.convertBase64ToS3(surveyJson[key], params);
                }
            }

            return surveyJson;
        },

        /**
         * @description Convert from base64 to blob file and upload to S3
         * @param {String} fileName
         * @param {String:Base64} value base64 file
         */
        async storeToS3(fileName, value, params = null) {
            let self = this;
            fileName += "_" + self.generateUniqid();
            let file = await self.base64ToFile(fileName, value);

            const response = await self.storeS3([{ file, fileName }], {
                name: "webform",
                id: self.webformId || params.webformId,
                path: self.webformS3Path || params.path
            });

            if (response && Array.isArray(response) && response[0].path) {
                return response[0].path;
            } else {
                console.error(response);
            }
        },
        addTimestampToUrl(url) {
            let ts = null;
            if (typeof this._now === "function") {
                ts = parseInt(this._now().toSeconds());
            } else {
                // Used on previsit form, helper not been rendered
                ts = DateTime.local().toSeconds();
            }
            // Add param to url to prevent retrieve from cache
            if (this.checkIsURL(url)) {
                url = new URL(url);
                url.searchParams.set("ts", ts);
                return url.href;
            }
            return url;
        },
        /**
         * Used from a component that not use helper.js
         */
        generateUniqid() {
            if (typeof this.uniqid === "function") {
                return this.uniqid();
            } else {
                // Used on previsit form, helper not been rendered
                return uniqid();
            }
        },
        doMarkdown(survey, options) {
            //convert the markdown text to html
            var str = converter.makeHtml(options.text);
            if (str.indexOf("<p>") == 0) {
                //remove root paragraphs <p></p>
                str = str.substring(3);
                str = str.substring(0, str.length - 4);
            }
            //set html
            options.html = str;
        },
        async navigateToUrls(webforms) {
            let self = this;
            let urls = webforms.map(webform => webform.navigateToUrl);
            // Remove empty url
            let validUrls = urls.filter(url => url && url.trim().length > 0);

            // Filter duplicates
            let uniqueUrls = [
                ...new Set(validUrls.map(url => self.normalizeUrl(url)))
            ];
            self.isRedirecting = true;
            self.uniqueUrls = uniqueUrls;
            const sleep = m => new Promise(r => setTimeout(r, m));
            await sleep(500);
            if (uniqueUrls.length > 0) {
                uniqueUrls.forEach(url => {
                    if (url) {
                        window.open(url, "_blank", "noopener,noreferrer");
                    }
                });
            }
            self.isRedirecting = false;
        },
        normalizeUrl(url) {
            try {
                if (!url.match(/^[a-zA-Z]+:\/\//)) {
                    url = "https://" + url;
                }
                let normalizedUrl = new URL(url);
                return normalizedUrl.href.toLowerCase().replace(/\/$/, "");
            } catch (error) {
                throw new Error("URL normalization failed: " + error.message);
            }
        },
        applyFallbackMaxSize(survey) {
            survey.getAllQuestions().forEach(q => {
                if (
                    q.getType?.() === "file" &&
                    (!q.maxSize || q.maxSize === 0)
                ) {
                    q.maxSize = this.FALLBACK_MAX;
                }
            });
        }
    }
};
